import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  trackEhrSectionSelectedSpec,
  trackEhrStartedSpec,
  trackFormTemplateFieldsUpdatedSpec,
  trackIntegrationCompletedSpec,
  trackIntegrationCreatedSpec,
  trackIntegrationDataCompletedSpec,
  trackIntegrationDataFailedSpec,
  trackIntegrationDataRetrievedSpec,
  trackIntegrationFailedSpec,
  trackIntegrationLinkedSpec,
  trackIntegrationPatientSelectedSpec,
  trackIntegrationSessionCreatedSpec,
  trackIntegrationStartedSpec,
  trackIntegrationSubmittedSpec,
  trackIntegrationUnlinkedSpec,
  trackIntegrationUpdatedSpec,
  trackIntegrationUserSearchedSpec,
  trackIntegrationWidgetAuthStepErroredSpec,
  trackIntegrationWidgetAuthStepMiscSpec,
  trackPushWarningResolvedSpec,
  trackPushWarningViewedSpec,
  type EhrSectionSelected,
  type EhrStarted,
  type FormTemplateFieldsUpdated,
  type IntegrationCompleted,
  type IntegrationCreated,
  type IntegrationDataCompleted,
  type IntegrationDataFailed,
  type IntegrationDataRetrieved,
  type IntegrationFailed,
  type IntegrationLinked,
  type IntegrationPatientSelected,
  type IntegrationSessionCreated,
  type IntegrationStarted,
  type IntegrationSubmitted,
  type IntegrationUnlinked,
  type IntegrationUpdated,
  type IntegrationUserSearched,
  type IntegrationWidgetAuthStepErrored,
  type IntegrationWidgetAuthStepMisc,
  type PushWarningResolved,
  type PushWarningViewed,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

export const integrationEvents: SnowplowEventMap = {
  // Datadog keeps this dedicated GDT event name. Snowplow maps it onto the
  // published integration_data_completed schema so the collector never
  // receives an unregistered self-describing event — GDT-specific dimensions
  // (pvs_system_name, gdt_version, record_type) stay Datadog-only since that
  // schema defines no field for arbitrary extra dimensions.
  //
  // gdt_file_parsed only ever fires for an inbound (PVS-to-Heidi) record —
  // the watcher filters out Heidi's own outbound 6310 export before this
  // event can fire — so every record type it reports is patient data and
  // `data_type: 'patient'` is the correct fixed label, not a placeholder.
  //
  // GDT export success/failure reuse the existing note_synced/note_sync_failed
  // events (see lib/snowplow/events/notes.ts) instead of a bespoke GDT event.
  [TrackingEventName.GdtFileParsed]: () => {
    trackIntegrationDataCompletedSpec({
      integration_type: 'gdt',
      data_type: 'patient',
    });
  },
  // Pre-push unlinked-sections warning funnel. `section_linked` (app name)
  // rides the existing `ehr_section_selected` schema.
  [TrackingEventName.PushWarningViewed]: (props) =>
    trackPushWarningViewedSpec(props as PushWarningViewed),
  [TrackingEventName.PushWarningResolved]: (props) =>
    trackPushWarningResolvedSpec(props as PushWarningResolved),
  [TrackingEventName.SectionLinked]: (props) =>
    trackEhrSectionSelectedSpec(props as EhrSectionSelected),
  [TrackingEventName.IntegrationCreated]: (props) =>
    trackIntegrationCreatedSpec(props as IntegrationCreated),
  [TrackingEventName.IntegrationStarted]: (props) =>
    trackIntegrationStartedSpec(props as IntegrationStarted),
  [TrackingEventName.IntegrationCompleted]: (props) =>
    trackIntegrationCompletedSpec(props as IntegrationCompleted),
  [TrackingEventName.IntegrationFailed]: (props) =>
    trackIntegrationFailedSpec(props as IntegrationFailed),
  [TrackingEventName.IntegrationLinked]: (props) =>
    trackIntegrationLinkedSpec(props as IntegrationLinked),
  [TrackingEventName.IntegrationUnlinked]: (props) =>
    trackIntegrationUnlinkedSpec(props as IntegrationUnlinked),
  [TrackingEventName.IntegrationSubmitted]: (props) =>
    trackIntegrationSubmittedSpec(props as IntegrationSubmitted),
  [TrackingEventName.IntegrationUpdated]: (props) =>
    trackIntegrationUpdatedSpec(props as IntegrationUpdated),
  [TrackingEventName.IntegrationDataCompleted]: (props) =>
    trackIntegrationDataCompletedSpec(props as IntegrationDataCompleted),
  [TrackingEventName.IntegrationPatientSelected]: (props) =>
    trackIntegrationPatientSelectedSpec(props as IntegrationPatientSelected),
  [TrackingEventName.IntegrationSessionCreated]: (props) =>
    trackIntegrationSessionCreatedSpec(props as IntegrationSessionCreated),
  [TrackingEventName.IntegrationUserSearched]: (props) =>
    trackIntegrationUserSearchedSpec(props as IntegrationUserSearched),
  [TrackingEventName.IntegrationWidgetAuthStepErrored]: (props) =>
    trackIntegrationWidgetAuthStepErroredSpec(props as IntegrationWidgetAuthStepErrored),
  [TrackingEventName.IntegrationWidgetAuthStepMisc]: (props) =>
    trackIntegrationWidgetAuthStepMiscSpec(props as IntegrationWidgetAuthStepMisc),
  [TrackingEventName.EhrStarted]: (props) => trackEhrStartedSpec(props as EhrStarted),
  [TrackingEventName.IntegrationDataFailed]: (props) =>
    trackIntegrationDataFailedSpec(props as IntegrationDataFailed),
  [TrackingEventName.IntegrationDataRetrieved]: (props) =>
    trackIntegrationDataRetrievedSpec(props as IntegrationDataRetrieved),
  [TrackingEventName.FormTemplateFieldsUpdated]: (props) =>
    trackFormTemplateFieldsUpdatedSpec(props as FormTemplateFieldsUpdated),
};
